import { api } from '@/lib/api';
import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mutationKeys } from '../mutationKeys';
import { queryKeys } from '../queryKeys';
import type {
  CreateQrCodeNoteRequest,
  CreateQrCodeRequest,
  QrCodeNoteResponse,
  QrCodeResponse,
  UpdateQrCodeNoteRequest,
} from './qrCodeTypes';

export const useCreateQrCodeMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.adminQrCodes.create(),
    mutationFn: async (data: CreateQrCodeRequest) => {
      return await api
        .post('admin/qr-codes', { json: data })
        .json<QrCodeResponse>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminQrCodes.all(),
      });
      toast.success('QR code created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create QR code');
    },
  });
};

export const useCreateQrCodeNoteMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.adminQrCodeNotes.create(),
    mutationFn: async (data: CreateQrCodeNoteRequest) => {
      return await api
        .post('admin/qr-code-notes', { json: data })
        .json<QrCodeNoteResponse>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminQrCodeNotes.all(),
      });
      toast.success('QR code note created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create QR code note');
    },
  });
};

export const useUpdateQrCodeNoteMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.adminQrCodeNotes.update(),
    mutationFn: async ({
      noteId,
      data,
    }: {
      noteId: number;
      data: UpdateQrCodeNoteRequest;
    }) => {
      return await api
        .put(`admin/qr-code-notes/${noteId}`, { json: data })
        .json<QrCodeNoteResponse>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminQrCodeNotes.all(),
      });
      toast.success('QR code note updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update QR code note');
    },
  });
};

export const useDeleteQrCodeNoteMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.adminQrCodeNotes.delete(),
    mutationFn: async (noteId: number) => {
      return await api.delete(`admin/qr-code-notes/${noteId}`).json<void>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminQrCodeNotes.all(),
      });
      toast.success('QR code note deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete QR code note');
    },
  });
};
